import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronRight, PencilLine } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { renderAsteriskBold } from '../utils/emphasis';
import { decodeEscapedHtml, sanitizeHtml } from '../utils/html';

export interface ProjectCriteriaFitRow {
  criterionKey: string;
  label: string;
  yourNeeds: string;
  providerFitHtml: string;
  showSeeMore?: boolean;
}

export interface ProjectCriteriaFitProps {
  rows?: ProjectCriteriaFitRow[];
  draftProviderFitByKey?: Record<string, string>;
  onDraftProviderFitChange?: (next: Record<string, string>) => void;
  onCancelDraft?: () => void;
  saveCommitId?: number;
}

export default function ProjectCriteriaFit({
  rows = [],
  draftProviderFitByKey,
  onDraftProviderFitChange,
  onCancelDraft,
  saveCommitId,
}: ProjectCriteriaFitProps) {
  const COLLAPSE_KEY = 'one_pager_section_expanded__project_criteria_fit';
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(COLLAPSE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const providerFitTextByKey = useMemo(() => {
    // Stored HTML can arrive entity-escaped; decode at the boundary so both the display
    // and the editor receive real HTML (BPP-226). Drafts are Quill output, already clean.
    const entries = rows.map((r) => [r.criterionKey, decodeEscapedHtml(r.providerFitHtml)] as const);
    return Object.fromEntries(entries);
  }, [rows]);

  const editValueByKey = useMemo(() => {
    return draftProviderFitByKey ?? providerFitTextByKey;
  }, [draftProviderFitByKey, providerFitTextByKey]);

  const startEditing = () => {
    setIsEditing(true);
    setIsExpanded(true);
    try {
      sessionStorage.setItem(COLLAPSE_KEY, 'true');
    } catch {
      // ignore
    }
  };

  const cancelEditing = () => {
    onCancelDraft?.();
    setIsEditing(false);
  };

  const finishEditing = () => {
    setIsEditing(false);
  };

  useEffect(() => {
    setIsEditing(false);
  }, [saveCommitId]);

  const toggleExpanded = () => {
    if (isEditing) return;
    setIsExpanded((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  return (
    <div ref={rootRef} className="w-full">
      <div className="flex items-start justify-between">
        <button type="button" className="flex items-start gap-[13px] text-left" onClick={toggleExpanded}>
          <div className="flex size-[24px] items-center justify-center">
            <div className="flex-none">
              {isExpanded ? <ChevronDown size={24} color="#25ADE3" /> : <ChevronRight size={24} color="#25ADE3" />}
            </div>
          </div>

          <p
            className="flex-1 font-['Roboto_Slab',serif] text-[24px] font-bold leading-[36px] tracking-[0.75px] text-[#132543]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            Project Criteria Fit
          </p>
        </button>

        {!isEditing && (
          <button
            type="button"
            className="inline-flex items-center gap-[6px] text-center font-['Inter',sans-serif] text-[14px] font-medium leading-[20px] tracking-[-0.1504px] text-[#53BEE8]"
            onClick={startEditing}
            data-pdf-exclude
          >
            <PencilLine size={16} color="#53BEE8" />
            Edit
          </button>
        )}

        {isEditing && (
          <div className="flex items-center gap-2">
            <button type="button" className="px-3 py-1 rounded bg-[#132543] text-white" onClick={finishEditing}>
              Save
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded border border-gray-200 text-[#132543]"
              onClick={cancelEditing}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div
        className={
          'rounded-[12px] bg-white border-[0.5px] border-solid border-[#A3A3A3] overflow-hidden transition-all duration-200 ease-in-out ' +
          (isExpanded ? 'mt-[24px] max-h-[2000px] opacity-100' : 'mt-0 max-h-0 opacity-0')
        }
      >
        <div className="overflow-hidden">
          <div className="grid h-[54px] grid-cols-[160px_1fr_24px_1fr] items-center gap-x-[40px] border-b-2 border-[#132543] bg-white px-[10px]">
            <div />
            <p
              className="text-left font-['Roboto_Slab',serif] text-[16px] font-semibold leading-[21px] tracking-[0.35px] text-[#132543]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Your Project Needs
            </p>
            <div />
            <p
              className="text-left font-['Roboto_Slab',serif] text-[16px] font-semibold leading-[21px] tracking-[0.35px] text-[#132543]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Provider Fit
            </p>
          </div>
        </div>

        <div className="border-t border-[#A3A3A3] border-t-[0.5px]">
          {rows.map((row, idx) => (
            <div
              key={row.criterionKey}
              className={idx === 0 ? '' : 'border-t border-[#A3A3A3] border-t-[0.5px]'}
            >
              <div className="grid grid-cols-[160px_1fr_24px_1fr] items-start gap-x-[40px] px-[10px] py-[24px]">
                <div>
                  <div className="flex items-start gap-[12px]">
                    <div className="mt-[3px] flex h-[21px] w-[21px] min-h-[21px] min-w-[21px] shrink-0 aspect-square items-center justify-center rounded-full bg-[#AB9151]">
                      <span className="block font-['Open_Sans',sans-serif] text-[11.9px] font-semibold leading-none text-white" style={{ fontVariationSettings: "'wdth' 100" }}>
                        {idx + 1}
                      </span>
                    </div>
                    <p
                      className="font-['Roboto_Slab',serif] text-[13px] font-semibold leading-[19.5px] tracking-[0.5px] text-[#132543]"
                      style={{ fontVariationSettings: "'wdth' 100" }}
                    >
                      {row.label}
                    </p>
                  </div>
                </div>

                <p
                  className="font-['Open_Sans',sans-serif] text-[14px] font-normal leading-[normal] text-[#132543]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {renderAsteriskBold(row.yourNeeds)}
                </p>

                <div className="flex h-full items-center justify-center">
                  <div className="flex size-[21px] items-center justify-center rounded-full bg-[#AB9151]/20">
                    <ArrowRight size={14} color="#AB9151" />
                  </div>
                </div>

                <div>
                  {!isEditing && (
                    <div
                      className="ql-snow font-['Open_Sans',sans-serif] text-[14px] font-normal leading-[normal] text-[#132543] [&_em]:italic [&_i]:italic [&_.ql-editor]:p-0"
                      style={{ fontVariationSettings: "'wdth' 100" }}
                      dangerouslySetInnerHTML={{
                        __html: `<div class="ql-editor">${sanitizeHtml(editValueByKey[row.criterionKey] ?? '')}</div>`,
                      }}
                    />
                  )}

                  {isEditing && (
                    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                      <ReactQuill
                        theme="snow"
                        value={editValueByKey[row.criterionKey] ?? ''}
                        onChange={(value) => {
                          const next = {
                            ...editValueByKey,
                            [row.criterionKey]: value,
                          };
                          onDraftProviderFitChange?.(next);
                        }}
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, false] }],
                            ['bold', 'italic', 'underline'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            ['link'],
                            ['clean'],
                          ],
                        }}
                      />
                    </div>
                  )}
                  {row.showSeeMore && !isEditing && (
                    <div className="mt-[8px] flex justify-end">
                      <button
                        type="button"
                        className="font-['Open_Sans',sans-serif] text-[14px] font-semibold leading-[normal] text-[#53BEE8]"
                        style={{ fontVariationSettings: "'wdth' 100" }}
                      >
                        see more {'\u2192'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
